import { useTranslation } from "react-i18next";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";
import { API_ORIGIN } from "@/lib/config";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ProfileDraft {
  displayName: string;
  bio: string;
}

/**
 * Demonstrates the foundation autosave primitive end-to-end: edit a field and
 * the visible status cycles Saving… → Saved, rapid edits debounce into one
 * write, and an in-flight write is aborted by the next one (last-write-wins).
 *
 * The persistence sink here is simulated (a short delay honouring the abort
 * signal) because the foundation API has no demo write endpoint — feature
 * slices wire `save` to a real TanStack Query mutation against their resource.
 * The shape of that wiring is exactly what this shows.
 */
export function AutosaveDemoPage() {
  const { t } = useTranslation();

  const { form, autosave } = useAutosaveForm<ProfileDraft>({
    defaultValues: { displayName: "", bio: "" },
    autosave: {
      debounceMs: 400,
      save: (_data, signal) =>
        new Promise<void>((resolve, reject) => {
          const id = setTimeout(resolve, 600);
          signal.addEventListener("abort", () => {
            clearTimeout(id);
            reject(new DOMException("aborted", "AbortError"));
          });
        }),
      // Exercise the teardown-flush path: on tab close / hide within the
      // debounce window, the latest draft is delivered via navigator.sendBeacon.
      // This demo posts to an illustrative sink; a feature slice points it at
      // its real resource endpoint (the same JSON the `save` mutation sends).
      beacon: (data) => ({
        url: `${API_ORIGIN}/api/v1/_autosave-demo/`,
        body: JSON.stringify(data),
        contentType: "application/json",
      }),
    },
  });

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>{t("demo.title")}</CardTitle>
            <SaveStatus status={autosave.status} />
          </div>
          <CardDescription>{t("demo.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="space-y-4"
          >
            <form.Field name="displayName">
              {(field) => <TextField field={field} label={t("demo.displayName")} />}
            </form.Field>
            <form.Field name="bio">
              {(field) => (
                <TextField
                  field={field}
                  label={t("demo.bio")}
                  placeholder={t("demo.bioPlaceholder")}
                />
              )}
            </form.Field>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
